import _ from 'lodash';
import Logger from '../util/Logger';
import { RoleType, roleTypeFromString } from './RoleType';
import DateTime from './DateTime';

/**
 * Methods for consistent access to User properties via server returned JSON.
 *
 * All interactions or accesses on User fields should be handled via helper method in this
 * class. Methods should only be to avoid `this` scoping issues in nested calls.
 *
 * Utility methods should throw in the case of unexpected constant mismatch such as missing data,
 * all calls to these methods should be guarded at the callsite.
 */
export default {

  isLiveEnabled (user) {
    if (!user) {
      Logger.assert('Cannot call isLiveEnabled() without a User object');
    } else {
      const features = user.features;
      return !!features && features.LIVE;
    }
  },

  /**
   * Extracts the tenant.id field from a given user json
   * @param {object} user User json
   * @returns {string} id of the user's tenant
   */
  getTenant (user) {
    if (!user) {
      Logger.assert('Cannot call getTenant() without a User object');
    } else {
      const tenant = user.tenant;
      if (!tenant) {
        Logger.assert('User does not have a Tenant', this.getId(user));
      } else {
        return tenant;
      }
    }
  },

  /**
   * Makes sure the User has a tenant
   * @param {object} user User json
   * @returns {boolean} has a tenant
   */
  hasTenant (user) {
    if (!user) {
      Logger.assert('Cannot call hasTenant() without a User object');
    } else {
      // We call this instead of getTenant because getTenant will throw
      return !!user.tenant;
    }
  },

  /**
   * Extracts the email
   *
   * @param {object} user User json
   * @returns {string} user email address
   */
  getEmail (user) {
    if (!user) {
      Logger.assert('Cannot call getEmail() without a User object');
    } else {
      const email = _.get(user, 'email');
      if (!email) {
        // Guests are expected to not have email
        if (!this.isGuest(user)) {
          Logger.error('User was missing an email', this.getId(user));
        }
        return '';
      } else {
        return email;
      }
    }
  },

  /**
   * Extracts the display name
   *
   * @param {object} user User json
   * @returns {string} user display name
   */
  getDisplayName (user) {
    if (!user) {
      Logger.assert('Cannot call getDisplayName() without a User object');
    } else {
      const name = _.get(user, 'display_name');
      if (!name) {
        Logger.error('User was missing a display_name', this.getId(user));
        return '';
      } else {
        return name;
      }
    }
  },

  /**
   * Extracts the id
   *
   * @param {object} user User json
   * @returns {string} user id
   */
  getId (user) {
    if (!user) {
      Logger.assert('Cannot call getId() without a User object');
    } else {
      const id = _.get(user, 'id');
      if (!id) {
        Logger.assert('User was missing an id');
      } else {
        return id;
      }
    }
  },

  /**
   * Extracts the media avatar url if it exists
   * @param {object} user User json
   * @returns {string} avatar url
   */
  getMediaAvatarPhoto (user) {
    if (!user) {
      Logger.assert('Cannot call getMediaAvatarPhoto() without a User object');
    } else {
      const avatar = _.get(user, 'media_avatar.photo');
      if (!avatar) {
        return '';
      } else {
        return avatar;
      }
    }
  },

  /**
   * Extracts the initials
   * @param {object} user User json
   * @returns {string} user initials
   */
  getInitials (user) {
    if (!user) {
      Logger.assert('Cannot call getInitials() without a User object');
    } else {
      const initials = _.get(user, 'initials');
      if (!initials) {
        Logger.error('User was missing initials', this.getId(user));
        return '';
      } else {
        return initials;
      }
    }
  },

  /**
   * Has manage launched
   *
   * @param {object} user User json
   * @returns {boolean} has this user launched via CS
   */
  hasManageLaunched (user) {
    if (!user) {
      Logger.assert('Cannot call hasManageLaunched() without a User object');
    } else {
      return !!_.get(user, 'has_manage_launched') || false;
    }
  },

  /**
   * Has self launched
   *
   * @param {object} user User json
   * @returns {boolean} has this user launched
   */
  hasSelfLaunched (user) {
    if (!user) {
      Logger.assert('Cannot call hasSelfLaunched() without a User object');
    } else {
      return !!_.get(user, 'has_self_launched') || false;
    }
  },

  /**
   * Is viewing as Admin
   *
   * @param {object} user User json
   * @returns {boolean} is this user viewing as Admin
   */
  isAdminViewingAs (user) {
    if (!user) {
      Logger.assert('Cannot call isAdminViewingAs() without a User object');
    } else {
      return !!_.get(user, 'admin_viewing_as');
    }
  },

  /**
   * Is viewing as Delegate
   *
   * @param {object} user User json
   * @returns {boolean} is this user viewing as a Delegate
   */
  isDelegatingAs (user) {
    if (!user) {
      Logger.assert('Cannot call isDelegatingAs() without a User object');
    } else {
      return !!_.get(user, 'delegate_user');
    }
  },

  /**
   * Get the delegate user
   *
   * @param {object} user User json
   * @returns {object | null} Delegate user if one exists
   */
  getDelegate (user) {
    if (!user) {
      Logger.assert('Cannot call getDelegate() without a User object');
    } else {
      return _.get(user, 'delegate_user') || null;
    }
  },

  /**
   * Is this user the tenant account owner
   * @param {object} user User json
   * @returns {boolean} is this user the tenant owner
   */
  isTenantAccountOwner (user) {
    if (!user) {
      Logger.assert('Cannot call isTenantAccountOwner() without a User object');
    } else {
      return !!_.get(user, 'is_tenant_account_owner');
    }
  },

  /**
   * Is this user a guest
   * @param {object} user User json
   * @returns {boolean} is this user a guest
   */
  isGuest (user) {
    if (!user) {
      Logger.assert('Cannot call isGuest() without a User object');
    } else {
      return !!_.get(user, 'guest') || false;
    }
  },

  /**
   * Is this user allowed to launch events
   * @param {object} user User json
   * @returns {boolean} is this user allowed to launch events
   */
  isEventCreationAllowed (user) {
    if (!user) {
      Logger.assert('Cannot call isEventCreationAllowed() without a User object');
    } else {
      return !!_.get(user, 'event_creation_allowed');
    }
  },

  /**
   * Get role type
   * @param {object} user User json
   * @returns {string} role type
   */
  getRoleType (user) {
    if (!user) {
      Logger.assert('Cannot call getRoleType() without a User object');
    } else {
      const role = _.get(user, 'role_type');
      if (!role) {
        Logger.error('User was missing role_type', this.getId(user));
        return RoleType.USER;
      } else {
        return roleTypeFromString(role);
      }
    }
  },

  /**
   * Get Admin added date
   * @param {object} user User json
   * @returns {moment.Moment} moment
   */
  getAdminAddedDate (user) {
    if (!user) {
      Logger.assert('Cannot call getAdminAddedDate() without a User object');
    } else {
      const date = _.get(user, 'admin_add_date');
      if (!date) {
        Logger.error('User was missing admin_add_date', this.getId(user));
        return null;
      } else {
        return DateTime.unixSecondsToMoment(date);
      }
    }
  },

  getPlanId (user) {
    if (!user) {
      Logger.assert('Cannot call getPlanId() without a User object');
    } else {
      const id = _.get(user, 'plan.id');
      if (!id) {
        Logger.error('User was missing plan.id', this.getId(user));
        return null;
      } else {
        return id;
      }
    }
  },

  getPlanName (user) {
    if (!user) {
      Logger.assert('Cannot call getPlanName() without a User object');
    } else {
      const name = _.get(user, 'plan.name');
      if (!name) {
        Logger.error('User was missing plan.name', this.getId(user));
        return null;
      } else {
        return name;
      }
    }
  },

  /**
   * Get User created date
   * @param {object} user User json
   * @returns {moment.Moment} moment
   */
  getCreatedDate (user) {
    if (!user) {
      Logger.assert('Cannot call getCreatedDate() without a User object');
    } else {
      const date = _.get(user, 'created_time');
      if (!date) {
        Logger.error('User was missing created_time', this.getId(user));
        return null;
      } else {
        return DateTime.unixSecondsToMoment(date);
      }
    }
  },

  /**
   * Get Hero authorized added date
   * @param {object} user User json
   * @returns {moment.Moment} moment
   */
  getHeroAuthorizedDate (user) {
    if (!user) {
      Logger.assert('Cannot call getHeroAuthorizedDate() without a User object');
    } else {
      const date = _.get(user, 'hero_authorized_date');
      if (!date) {
        Logger.error('User was missing hero_authorized_date', this.getId(user));
        return null;
      } else {
        return DateTime.unixSecondsToMoment(date);
      }
    }
  }
};
